import type {
  AgentBudgetCurrency,
  AgentBudgetView,
  AgentBudgetsResponse,
} from "@bash/shared";
import { getRequiredAccessToken } from "./client";
import { apiUrl, responseSnippet } from "./base";

/**
 * Settings → Agent budgets (agent wallet). A dedicated fetch, like engine.ts: the 404 of a dark flag is a TYPED
 * outcome that hides the section, and stays distinguishable from a failing server without reading any message text.
 */
export type AgentBudgetsStatus =
  { available: false } | { available: true; body: AgentBudgetsResponse };

/**
 * A save the server took (with the stored view), or the one refusal Settings says in its own words: this agent's
 * month already has a held or paid purchase in the other currency (409 currency_locked). `budget` is null when the
 * save landed but the server could not read the view back (it answers `{ outcome: "saved" }` with the row it wrote):
 * stored all the same, and the section reads its figures again.
 */
export type AgentBudgetSave =
  | { outcome: "saved"; budget: AgentBudgetView | null }
  | { outcome: "currency_locked" };

const PATH = "/api/agent-budgets";
const deadline = () => AbortSignal.timeout(20_000);

async function authHeader(): Promise<Record<string, string>> {
  return { Authorization: `Bearer ${await getRequiredAccessToken()}` };
}

/** GET. 404 (the server flag is off) means the section renders nothing. */
export async function getAgentBudgets(): Promise<AgentBudgetsStatus> {
  const res = await fetch(apiUrl(PATH), {
    headers: await authHeader(),
    credentials: "include",
    signal: deadline(),
  });
  if (res.status === 404) return { available: false };
  if (!res.ok) throw new Error(`${res.status} ${await responseSnippet(res)}`);
  return { available: true, body: (await res.json()) as AgentBudgetsResponse };
}

/**
 * PUT one agent's budget (null clears it). 409 currency_locked is a STATE, read from the structured field, never
 * from the message; any other non-2xx throws.
 */
export async function putAgentBudget(
  agentKey: string,
  monthlyBudgetMinor: number | null,
  currency: AgentBudgetCurrency
): Promise<AgentBudgetSave> {
  const res = await fetch(apiUrl(`${PATH}/${encodeURIComponent(agentKey)}`), {
    method: "PUT",
    headers: { ...(await authHeader()), "Content-Type": "application/json" },
    credentials: "include",
    signal: deadline(),
    body: JSON.stringify({ monthlyBudgetMinor, currency }),
  });
  if (res.ok) {
    const body = (await res.json()) as AgentBudgetView | { outcome: "saved" };
    return { outcome: "saved", budget: "outcome" in body ? null : body };
  }
  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as { error?: unknown };
    if (body.error === "currency_locked") return { outcome: "currency_locked" };
  }
  throw new Error(`${res.status} ${await responseSnippet(res)}`);
}
